import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import type { SiteManifest, SiteScene } from './SiteManifest';
import { SyncedAudio } from './SyncedAudio';
import { makeOccluder } from '../render/OccluderMesh';
import { particleOpacityClip } from './ParticleOpacity';

/**
 * glTFロード、AnimationMixer、同期再生(計画書§6)。
 * コンテンツは contentGroup(ENU座標系: +X=東, +Y=上, +Z=南)に配置される。
 * ポーズの出所(パスA/B)は一切関知しない — PoseProvider抽象を壊さないこと。
 */
export class SceneRunner {
  readonly contentGroup = new THREE.Group();
  readonly audio: SyncedAudio;
  /** 画面外インジケータの対象(manifestでindicator指定されたエンティティ) */
  readonly indicatorTargets: { label: string; object: THREE.Object3D }[] = [];
  /** 実行時スケール対象のジオメトリ(manifestでvisualScale=trueのエンティティ) */
  private visualScaleGeometries: THREE.BufferGeometry[] = [];
  private appliedVisualScale = 1;
  private mixers: THREE.AnimationMixer[] = [];
  private loader: GLTFLoader;
  private disposables: (() => void)[] = [];
  private scene: SiteScene;

  constructor(
    manifest: SiteManifest,
    renderer: THREE.WebGLRenderer,
    sceneId?: string,
  ) {
    const scene = sceneId ? manifest.scenes.find((s) => s.id === sceneId) : manifest.scenes[0];
    if (!scene) throw new Error(`シーンが見つかりません: ${sceneId ?? '(先頭)'}`);
    this.scene = scene;
    this.audio = new SyncedAudio(scene.loopSeconds);
    this.contentGroup.name = 'shiseki-content';

    const draco = new DRACOLoader().setDecoderPath('/draco/');
    const ktx2 = new KTX2Loader().setTranscoderPath('/basis/').detectSupport(renderer);
    this.loader = new GLTFLoader().setDRACOLoader(draco).setKTX2Loader(ktx2);
    this.disposables.push(() => {
      draco.dispose();
      ktx2.dispose();
    });
  }

  /** モデル・オクルーダー・音声をすべてロードする */
  async load(baseUrl: string, onProgress?: (loaded: number, total: number) => void): Promise<void> {
    const jobs: Promise<void>[] = [];
    const total = this.scene.entities.length + this.scene.occluders.length + 1;
    let done = 0;
    const tick = () => onProgress?.(++done, total);

    for (const entity of this.scene.entities) {
      jobs.push(
        this.loader.loadAsync(baseUrl + entity.model).then((gltf) => {
          const root = gltf.scene;
          const opacityClip = particleOpacityClip(root, this.scene.loopSeconds);
          // ENU → three.js: x=east, y=up, z=-north
          root.position.set(entity.position.east, entity.position.up, -entity.position.north);
          root.rotation.y = (entity.rotationYDeg * Math.PI) / 180;
          root.scale.setScalar(entity.scale);
          // アニメーションで大きく移動するモデルがバウンディング判定の揺らぎで
          // 消えないよう、エンティティはフラスタムカリングの対象外にする(保険)
          root.traverse((o) => {
            if (o instanceof THREE.Mesh) {
              o.frustumCulled = false;
              // 半透明(飛行機雲等)は深度書き込みを切って相互のちらつきを防ぐ
              const mats = Array.isArray(o.material) ? o.material : [o.material];
              for (const m of mats) if (m.transparent) m.depthWrite = false;
            }
          });
          this.contentGroup.add(root);
          this.disposables.push(() => disposeObject(root));

          if (entity.indicator) {
            // 追跡対象はアニメーションで動く最初のメッシュノード(rootは静的原点)
            let target: THREE.Object3D = root;
            root.traverse((o) => {
              if (target === root && o instanceof THREE.Mesh) target = o;
            });
            this.indicatorTargets.push({ label: entity.indicator, object: target });
          }

          if (entity.visualScale) {
            const geoms: THREE.BufferGeometry[] = [];
            root.traverse((o) => {
              if (o instanceof THREE.Mesh) geoms.push(o.geometry);
            });
            this.visualScaleGeometries.push(...geoms);
            // ロード完了前にsetVisualScaleが呼ばれていた場合に追随させる
            const k = this.appliedVisualScale;
            if (k !== 1) for (const g of geoms) g.scale(k, k, k);
          }

          if (gltf.animations.length > 0 || opacityClip) {
            const mixer = new THREE.AnimationMixer(root);
            const clips =
              entity.animation === '*'
                ? [...gltf.animations]
                : gltf.animations.filter((c) => c.name === entity.animation);
            if (opacityClip) clips.push(opacityClip);
            if (clips.length === 0) {
              console.warn(`[SceneRunner] AnimationClip "${entity.animation}" が ${entity.model} にありません`);
            }
            for (const clip of clips) {
              const action = mixer.clipAction(clip);
              action.setLoop(THREE.LoopRepeat, Infinity);
              action.play();
            }
            this.mixers.push(mixer);
          }
          tick();
        }),
      );
    }

    for (const occluderPath of this.scene.occluders) {
      jobs.push(
        this.loader.loadAsync(baseUrl + occluderPath).then((gltf) => {
          const occ = makeOccluder(gltf.scene);
          this.contentGroup.add(occ);
          this.disposables.push(() => disposeObject(occ));
          tick();
        }),
      );
    }

    jobs.push(this.audio.load(baseUrl, this.scene.audio).then(tick));
    await Promise.all(jobs);
  }

  /** 体験開始(ユーザージェスチャー内で呼ぶこと) */
  async start(): Promise<void> {
    await this.audio.start();
  }

  /**
   * 毎フレーム呼ぶ。音声クロックの位相 t にアニメーションを強制追従させる。
   * mixer.update(delta)の累積ではなくsetTime方式 — ドリフトが構造的に発生しない。
   */
  update(): void {
    const t = this.audio.getTime();
    for (const mixer of this.mixers) {
      mixer.setTime(t);
    }
  }

  get loopSeconds(): number {
    return this.scene.loopSeconds;
  }

  /**
   * 見かけスケールを設定する(1 = 実寸)。visualScale指定エンティティの
   * ジオメトリ頂点に適用するため、軌道やscaleアニメーションとは干渉しない。
   */
  setVisualScale(mult: number): void {
    const m = Math.max(0.1, mult);
    const r = m / this.appliedVisualScale;
    if (Math.abs(r - 1) < 1e-6) return;
    for (const g of this.visualScaleGeometries) g.scale(r, r, r);
    this.appliedVisualScale = m;
  }

  dispose(): void {
    for (const mixer of this.mixers) mixer.stopAllAction();
    this.mixers = [];
    for (const d of this.disposables) d();
    this.disposables = [];
    this.audio.dispose();
  }
}

/** three.jsリソースのdispose徹底(モバイルのメモリ制約) */
export function disposeObject(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry?.dispose();
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const m of mats) {
        for (const value of Object.values(m)) {
          if (value instanceof THREE.Texture) value.dispose();
        }
        m.dispose();
      }
    }
  });
  root.removeFromParent();
}
